import React, { useCallback } from 'react';

import { Checkbox } from 'ui-components/components/checkbox';
import { FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import {
  BRANDING_CUSTOM_COLORS_TOGGLE,
  BRANDING_CUSTOM_TEMPLATE_TOGGLE,
  BRANDING_OVERRIDE_SETTINGS_TOGGLE,
  BRANDING_PREVIEW_CLICK,
  BRANDING_SHOW_LOGO_TOGGLE,
  BRANDING_SHOW_ORDER_DETAILS_TOGGLE,
  BRANDING_SHOW_PROMO_IMAGE_TOGGLE,
  BRANDING_SHOW_SERVICE_MENU_TOGGLE,
  BRANDING_SHOW_SOCIAL_TOGGLE,
} from '../../../../../constants/analytics-events';
import { Segment } from '../../../../../services/analytics-services/segment';
import { EventSourceStrings } from '../../../../../tracking/tracking-constants';
import messages from './messages';
import style from './style.module.less';
import { useBTEmailExperienceNew } from '../../../../../hooks/use-seller-setting';
import { useIsBrandingTrackingEnabledForSeller } from '../../../branding-defaults/use-is-branding-tracking-enabled-for-seller';
import { useUpdateBrandingContext } from '../../../components/branding/context';
import CustomColorPicker from '../../../components/branding/custom-color-picker';
import CustomOrderDetails from '../../../components/branding/custom-order-details';
import { CustomPromoImage } from '../../../components/branding/custom-promo-image';
import CustomServiceMenu from '../../../components/branding/custom-service-menu';
import { CustomTemplateLayout } from '../../../components/branding/custom-template-layout';
import PreviewLink from '../../../components/branding/preview-link';
import { SettingContent, SettingDescription, SettingHeader, SettingsRow } from '../../../components/settings-elements';
import { useStoreUpdateContext } from '../context';

/**
 * @param {object} props
 * @param {object} props.customBranding - Custom branding configuration object with properties like show_logo, custom_colors, show_social, service_menu, show_details, show_promo_image, and show_custom_template_layout
 * @param {object} props.brandingSettings - Branding settings object containing useCustomBrandingSettings and other configuration
 * @param {string | null} [props.returnMessage] - Return message
 * @returns {React.ReactNode}
 */
export const BrandedPagesCustomSettings = ({ customBranding, brandingSettings, returnMessage = null }) => {
  const { updateCustomBranding } = useUpdateBrandingContext();
  const { updateStoreSetting } = useStoreUpdateContext();
  const { hasBaseFeatures: hasBrandedTrackingFeature, hasEnhancedFeatures: showBrandedTrackingEnhancements } =
    useIsBrandingTrackingEnabledForSeller();
  const enableNewEmailExperience = useBTEmailExperienceNew();

  const hasCustomBrandingSettings = brandingSettings.useCustomBrandingSettings;
  const GROUP_NAME = 'brandingSettings';

  // top level switches
  const {
    show_logo: showLogo,
    custom_colors: customColors,
    show_social: showSocial,
    service_menu: showServiceMenu,
    show_details: showOrderDetails,
    show_promo_image: showPromoImage,
    show_custom_template_layout: showCustomTemplateLayout,
  } = customBranding;

  const toggleBrandingSettings = useCallback(() => {
    Segment.trackEvent(BRANDING_OVERRIDE_SETTINGS_TOGGLE, {
      isEnabled: !hasCustomBrandingSettings,
      source: EventSourceStrings.STORE_BRANDING,
    });
    updateStoreSetting(GROUP_NAME, 'useCustomBrandingSettings', !hasCustomBrandingSettings);
  }, [hasCustomBrandingSettings, updateStoreSetting]);

  const handlePreviewClick = useCallback(() => {
    Segment.trackEvent(BRANDING_PREVIEW_CLICK, { source: EventSourceStrings.STORE_BRANDING });
  }, []);

  const toggleCustomTemplateLayout = useCallback(() => {
    Segment.trackEvent(BRANDING_CUSTOM_TEMPLATE_TOGGLE, {
      isEnabled: !showCustomTemplateLayout,
      source: EventSourceStrings.STORE_BRANDING,
    });
    updateCustomBranding('show_custom_template_layout', !showCustomTemplateLayout);
  }, [showCustomTemplateLayout, updateCustomBranding]);

  const toggleCustomColors = useCallback(() => {
    Segment.trackEvent(BRANDING_CUSTOM_COLORS_TOGGLE, { isEnabled: !customColors, source: EventSourceStrings.STORE_BRANDING });
    updateCustomBranding('custom_colors', !customColors);
  }, [customColors, updateCustomBranding]);

  const toggleShowLogo = useCallback(() => {
    Segment.trackEvent(BRANDING_SHOW_LOGO_TOGGLE, { isEnabled: !showLogo, source: EventSourceStrings.STORE_BRANDING });
    updateCustomBranding('show_logo', !showLogo);
  }, [showLogo, updateCustomBranding]);

  const toggleShowSocial = useCallback(() => {
    Segment.trackEvent(BRANDING_SHOW_SOCIAL_TOGGLE, { isEnabled: !showSocial, source: EventSourceStrings.STORE_BRANDING });
    updateCustomBranding('show_social', !showSocial);
  }, [showSocial, updateCustomBranding]);

  const toggleShowServiceMenu = useCallback(() => {
    Segment.trackEvent(BRANDING_SHOW_SERVICE_MENU_TOGGLE, { isEnabled: !showServiceMenu, source: EventSourceStrings.STORE_BRANDING });
    updateCustomBranding('service_menu', !showServiceMenu);
  }, [showServiceMenu, updateCustomBranding]);

  const toggleShowPromoImage = useCallback(() => {
    Segment.trackEvent(BRANDING_SHOW_PROMO_IMAGE_TOGGLE, { isEnabled: !showPromoImage, source: EventSourceStrings.STORE_BRANDING });
    updateCustomBranding('show_promo_image', !showPromoImage);
  }, [showPromoImage, updateCustomBranding]);

  const toggleShowOrderDetails = useCallback(() => {
    Segment.trackEvent(BRANDING_SHOW_ORDER_DETAILS_TOGGLE, { isEnabled: !showOrderDetails, source: EventSourceStrings.STORE_BRANDING });
    updateCustomBranding('show_details', !showOrderDetails);
  }, [showOrderDetails, updateCustomBranding]);

  return (
    <>
      <FlexRow className={style['custom-branding-settings-header']}>
        <Text textType={TextType.H2} value={messages.customBrandingTitle} />
        <PreviewLink
          brandingSettings={brandingSettings}
          customBranding={customBranding}
          disabled={!hasCustomBrandingSettings}
          onPreviewClick={handlePreviewClick}
          returnMessage={returnMessage}
        />
      </FlexRow>
      <div className={style['custom-branding-container']}>
        <div className={style.description}>
          <Text element='span' value={messages.customBrandingDescription} />
          {enableNewEmailExperience ? <Text element='span' value={messages.previewNote} /> : null}
        </div>
        <Checkbox
          checked={hasCustomBrandingSettings}
          className={style['sub-checkbox']}
          disabled={!hasBrandedTrackingFeature}
          label={<Text textType={TextType.Paragraph} value={messages.overrideBrandingSettings} />}
          onChange={toggleBrandingSettings}
        />

        <div className={style['settings-box']}>
          {showBrandedTrackingEnhancements ? (
            <SettingsRow>
              <SettingHeader
                checked={showCustomTemplateLayout}
                disabled={!hasBrandedTrackingFeature || !hasCustomBrandingSettings}
                label={<Text textType={TextType.H4} value={messages.showCustomTemplateLayout} />}
                onCheck={toggleCustomTemplateLayout}
              />
              <SettingContent disabled={!showCustomTemplateLayout || !hasCustomBrandingSettings}>
                <CustomTemplateLayout
                  customBranding={customBranding}
                  disabled={!hasBrandedTrackingFeature || !showCustomTemplateLayout || !hasCustomBrandingSettings}
                  source={EventSourceStrings.STORE_BRANDING}
                />
              </SettingContent>
            </SettingsRow>
          ) : null}

          <SettingsRow>
            <SettingHeader
              checked={customColors}
              disabled={!hasBrandedTrackingFeature || !hasCustomBrandingSettings}
              label={<Text textType={TextType.H4} value={messages.customColors} />}
              onCheck={toggleCustomColors}
            />
            <SettingDescription>
              <Text textType={TextType.Paragraph} value={messages.customColorsDesc} />
            </SettingDescription>
            <SettingContent disabled={!hasBrandedTrackingFeature || !customColors || !hasCustomBrandingSettings}>
              <CustomColorPicker customBranding={customBranding} disabled={!hasBrandedTrackingFeature || !hasCustomBrandingSettings} />
            </SettingContent>
          </SettingsRow>

          <SettingsRow>
            <SettingHeader
              checked={showLogo}
              disabled={!hasBrandedTrackingFeature || !hasCustomBrandingSettings}
              label={<Text textType={TextType.H4} value={messages.showStoreLogo} />}
              onCheck={toggleShowLogo}
            />
            <SettingDescription>
              <Text textType={TextType.Paragraph} value={messages.showStoreLogoDesc} />
            </SettingDescription>
          </SettingsRow>

          <SettingsRow>
            <SettingHeader
              checked={showSocial}
              disabled={!hasBrandedTrackingFeature || !hasCustomBrandingSettings}
              label={<Text textType={TextType.H4} value={messages.showSocialLinks} />}
              onCheck={toggleShowSocial}
            />
            <SettingDescription>
              <Text textType={TextType.Paragraph} value={messages.showSocialLinksDesc} />
            </SettingDescription>
          </SettingsRow>

          <SettingsRow>
            <SettingHeader
              checked={showServiceMenu}
              disabled={!hasBrandedTrackingFeature || !hasCustomBrandingSettings}
              label={<Text textType={TextType.H4} value={messages.showServiceMenu} />}
              onCheck={toggleShowServiceMenu}
            />
            <SettingContent disabled={!showServiceMenu || !hasCustomBrandingSettings || !hasBrandedTrackingFeature}>
              <CustomServiceMenu
                customBranding={customBranding}
                disabled={!hasBrandedTrackingFeature || !hasCustomBrandingSettings}
                returnMessage={returnMessage}
                showReturnPolicySection
                source={EventSourceStrings.STORE_BRANDING}
              />
            </SettingContent>
          </SettingsRow>

          {showBrandedTrackingEnhancements ? (
            <SettingsRow>
              <SettingHeader
                checked={showPromoImage}
                disabled={!hasBrandedTrackingFeature || !hasCustomBrandingSettings}
                label={<Text textType={TextType.H4} value={messages.showPromoImage} />}
                onCheck={toggleShowPromoImage}
              />
              <SettingDescription>
                <Text textType={TextType.Paragraph} value={messages.showPromoImageDesc} />
              </SettingDescription>
              <SettingContent disabled={!showPromoImage || !hasCustomBrandingSettings || !hasBrandedTrackingFeature}>
                <CustomPromoImage customBranding={customBranding} disabled={!showPromoImage} source={EventSourceStrings.STORE_BRANDING} />
              </SettingContent>
            </SettingsRow>
          ) : null}

          <SettingsRow>
            <SettingHeader
              checked={showOrderDetails}
              disabled={!hasBrandedTrackingFeature || !hasCustomBrandingSettings}
              label={<Text textType={TextType.H4} value={messages.showOrderDetails} />}
              onCheck={toggleShowOrderDetails}
            />
            <SettingDescription>
              <Text textType={TextType.Paragraph} value={messages.showOrderDetailsDesc} />
            </SettingDescription>
            <SettingContent disabled={!hasBrandedTrackingFeature || !hasCustomBrandingSettings}>
              <CustomOrderDetails
                customBranding={customBranding}
                disabled={!hasBrandedTrackingFeature || !hasCustomBrandingSettings}
                source={EventSourceStrings.STORE_BRANDING}
              />
            </SettingContent>
          </SettingsRow>
        </div>
      </div>
    </>
  );
};
